import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FacebookIcon from '@mui/icons-material/Facebook';
import ReactMarkdown from 'react-markdown';

import API from '../../API';
import { newsImageUrl } from '../../utils/newsImage';

const formatPostDate = (raw: string): string => {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const normalizeBody = (body: string): string => body.replace(/\n+/g, '\n\n');

const NewsPage = () => {
  const [posts, setPosts] = useState<INewsItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // News is global — same posts on both shops — so the call
        // doesn't take a tenant. Server returns newest-first; no
        // client-side sort. 200 is generous for the current archive;
        // server-side pagination kicks in if/when the feed grows.
        const data = await API.getNews(200, 0);
        if (!cancelled) {
          setPosts(data.items);
        }
      } catch (err) {
        // Leave posts empty.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll to anchored item once posts are in the DOM. The global
  // ScrollToHash runs too early (before fetch completes) so it can't find
  // the element — this effect closes that race.
  useEffect(() => {
    if (!loaded || !hash) return;
    const id = hash.replace(/^#/, '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loaded, hash]);

  return (
    <Box sx={{ backgroundColor: '#fafafa', minHeight: '70vh' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: '0.2em', color: '#64748b' }}
        >
          News
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
          From the shop
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b', mb: 6 }}>
          Store updates, new arrivals, and the occasional rant.
        </Typography>

        {loaded && posts.length === 0 && (
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Nothing to share just yet — check back soon.
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {posts.map((post, idx) => {
            const firstImage = post.images?.[0];
            const isLast = idx === posts.length - 1;
            return (
              <Box
                key={post.id}
                id={post.id}
                sx={{
                  mt: idx === 0 ? 0 : 6,
                  pb: isLast ? 0 : 6,
                  borderBottom: isLast ? 'none' : '1px solid #e5e7eb',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    display: 'block',
                    mb: firstImage ? 2 : 1.5,
                  }}
                >
                  {formatPostDate(post.date)}
                </Typography>
                {firstImage && (
                  <Box
                    component="img"
                    src={newsImageUrl(firstImage)}
                    alt=""
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      mb: 3,
                    }}
                  />
                )}
                <Box
                  sx={{
                    color: '#1f2937',
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    '& p': { m: 0, mb: 1.5, '&:last-child': { mb: 0 } },
                    '& ul, & ol': { my: 1.5, pl: 3 },
                    '& li': { mb: 0.5 },
                    '& strong': { fontWeight: 700 },
                    '& em': { fontStyle: 'italic' },
                    '& a': { color: '#0f172a', textDecoration: 'underline' },
                    '& h1, & h2, & h3, & h4': { fontWeight: 700, fontSize: '1.125rem', m: 0, mt: 2, mb: 1 },
                    '& code': { backgroundColor: '#f1f5f9', px: 0.5, borderRadius: 0.5, fontSize: '0.9em' },
                    '& blockquote': {
                      borderLeft: '3px solid #cbd5e1',
                      pl: 2,
                      ml: 0,
                      my: 2,
                      color: '#475569',
                      fontStyle: 'italic',
                    },
                  }}
                >
                  <ReactMarkdown>{normalizeBody(post.body)}</ReactMarkdown>
                </Box>
                {post.facebook && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      component="a"
                      href={post.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      aria-label="View on Facebook"
                      sx={{ color: '#94a3b8', '&:hover': { color: '#1877f2' } }}
                    >
                      <FacebookIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default NewsPage;
